/**
 * advance-shield-regen.ts — per-tick ship shield recovery (v3M3,
 * MECHANICS §1.1).
 *
 * Each live in-system ship adds `effectiveMaxShields × shieldRegenRate`
 * to its `currentShields`, clamped at the cap. Regen rate scales with
 * the empire's Shield Mk research per `shieldRegenRate`:
 *   Mk 1 = 1%/tick, Mk 2 = 2%/tick, …, Mk 6 = 6%/tick (capped).
 *
 * v3M3 design lock (2026-04-28):
 *   - Cloak does NOT pause regen — coherent with energy regen (energy
 *     regen is suppressed during cloak because cloak drains energy;
 *     shields are an independent system, no analogous coupling).
 *   - Firing a weapon does NOT pause regen — `lastFiredTick` is the
 *     stealth-coupling field per MECHANICS §2.1, not a shield-coupling
 *     field. A "shields-down-after-firing" mechanic, if ever wanted,
 *     belongs to v3M10 balance pass.
 *
 * Skip conditions (no regen this tick):
 *   - Mid-jump (`systemId === null`) — the ship is in hyperspace.
 *   - EMP-disabled (`disabledTicks > 0`) — shields offline along with
 *     the rest of the ship's systems (MECHANICS §2.2).
 *   - Already destroyed (`currentHull <= 0`) — defensive; a destroyed
 *     ship should be despawned by combat/bombardment but the guard
 *     keeps stale entries safe.
 *
 * Pipeline placement: runs immediately after `advanceShipEnergyRegen`
 * so the two recovery passes sit together for readability and so a
 * ship that auto-decloaked this tick gets BOTH regen passes on the
 * same frame.
 */

import type { GameState } from '../state/types';
import { effectiveMaxShields, shieldRegenRate } from './research-effects';
import { auraBonusesForShip } from './auras';

/** Restore currentShields by `regenRate × effectiveMax`, clamped at the cap. */
export function advanceShipShieldRegen(state: GameState): void {
  for (const ship of state.ships.values()) {
    if (ship.systemId === null) continue; // mid-jump
    if (ship.disabledTicks > 0) continue; // EMP offline
    if (ship.currentHull <= 0) continue; // destroyed (defensive)

    const empire = state.empires.get(ship.ownerEmpireId);
    if (!empire) continue;
    const tmpl = state.shipTemplates.get(empire.raceId + ':' + ship.chassis);
    if (!tmpl) continue;

    // MFix8.5 — Bulwark-style auras: shieldMult expands the cap; a ship
    // already at its non-aura cap can suddenly regen toward a higher
    // cap when an emitter enters range. shieldRegenMult speeds the rate.
    const aura = auraBonusesForShip(state, ship);
    const cap = effectiveMaxShields(state, empire, tmpl) * aura.shieldMult;
    if (cap <= 0) continue; // chassis has no shields (e.g. unarmed scaffold)
    if (ship.currentShields >= cap) continue; // already at cap

    const rate = shieldRegenRate(state, empire) * aura.shieldRegenMult;
    const next = ship.currentShields + cap * rate;
    ship.currentShields = next > cap ? cap : next;
  }
}
